/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { interval, Subscription } from 'rxjs';
import { Geolocation } from '@capacitor/geolocation';
import { UserModel } from '@shared/models/user.model';
import { environment } from 'src/environments/environment';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private locRefreshSubscription: Subscription = new Subscription();
  public lastLocation: any = null;
  private teleportLocation: any = null;

  constructor(private userModel: UserModel) {
    //this.testEcho();
    this.getRead();

    Preferences.get({ key: 'TELEPORT_LOCATION' }).then((result) => {
      const val = result.value ? JSON.parse(result.value) : null;
      if (val != null) {
        // Garante compatibilidade com dados antigos que não têm o campo address
        if (!val.address) {
          val.address = null;
        }
        console.log('teleportLocation restored from Preferences: ' + JSON.stringify(val));
        this.teleportLocation = val;
      }
    });
  }


  public setTeleportationLocation(lon: number, lat: number, address?: string) {
    this.teleportLocation = { 
      type: 'Point', 
      coordinates: [lon, lat],
      address: address || null
    };

    Preferences.set({ key: 'TELEPORT_LOCATION', value: JSON.stringify(this.teleportLocation) }).then(() => {
      console.log('teleportLocation saved to Preferences');
    });
  }

  public getTeleportationLocation(): any {
    return this.teleportLocation;
  }

  public getTeleportationAddress(): string | null {
    return this.teleportLocation?.address || null;
  }

  /**
   * Restaura o estado de teletransporte a partir do userData (ex.: mesmo usuário logando de novo).
   * Chamar quando carregar userData do servidor e userData.usingTeleportation && userData.location.
   * Aceita location com coordinates [lon, lat] (GeoJSON) ou com propriedades lat/lon.
   */
  public async restoreTeleportationFromUserData(userData: any): Promise<void> {
    if (!userData?.usingTeleportation || !userData?.location) {
      return;
    }
    const loc = userData.location;
    let lon: number;
    let lat: number;
    if (loc.coordinates && Array.isArray(loc.coordinates) && loc.coordinates.length >= 2) {
      // GeoJSON: coordinates é [lon, lat]
      lon = Number(loc.coordinates[0]);
      lat = Number(loc.coordinates[1]);
    } else if (typeof loc.lon === 'number' && typeof loc.lat === 'number') {
      lon = loc.lon;
      lat = loc.lat;
    } else if (typeof loc.longitude === 'number' && typeof loc.latitude === 'number') {
      lon = loc.longitude;
      lat = loc.latitude;
    } else {
      return;
    }
    if (isNaN(lon) || isNaN(lat)) {
      return;
    }
    this.teleportLocation = {
      type: loc.type || 'Point',
      coordinates: [lon, lat],
      address: loc.address ?? null
    };
    await Preferences.set({ key: 'TELEPORT_LOCATION', value: JSON.stringify(this.teleportLocation) });
    console.log('Teleportation restored from userData on login');
  }

  /**
   * Restaura teletransporte a partir do backup salvo no logout (mesmo usuário).
   * Chamar no login quando houver userId; se existir backup, restaura mesmo que o servidor
   * não tenha enviado usingTeleportation (ex.: API de login não retorna esse campo).
   */
  public async restoreTeleportationFromBackup(userData: any): Promise<boolean> {
    const userId = LocationService.getUserIdFromUser(userData);
    if (!userId) {
      return false;
    }
    const key = LocationService.teleportBackupKey(userId);
    const backup = await Preferences.get({ key });
    if (!backup?.value) {
      return false;
    }
    const loc = JSON.parse(backup.value);
    if (!loc?.coordinates || !Array.isArray(loc.coordinates) || loc.coordinates.length < 2) {
      return false;
    }
    this.teleportLocation = {
      type: loc.type || 'Point',
      coordinates: loc.coordinates,
      address: loc.address ?? null
    };
    await Preferences.set({ key: 'TELEPORT_LOCATION', value: JSON.stringify(this.teleportLocation) });
    await Preferences.remove({ key });
    console.log('Teleportation restored from backup on login, key was: ' + key);
    return true;
  }

  /**
   * Verifica se a localização do userData (teleportada) é diferente da localização atual (lastLocation).
   * Usado para decidir se deve reverter teletransporte para a localização atual quando a assinatura expira.
   */
  public isTeleportLocationDifferentFromCurrent(userDataLocation: any): boolean {
    if (!userDataLocation?.coordinates || !this.lastLocation?.coordinates) {
      return true;
    }
    const tol = 1e-4;
    return (
      Math.abs(this.lastLocation.coordinates[0] - userDataLocation.coordinates[0]) > tol ||
      Math.abs(this.lastLocation.coordinates[1] - userDataLocation.coordinates[1]) > tol
    );
  }

  /**
   * Limpa apenas o estado de teletransporte (TELEPORT_LOCATION, ORIGINAL_LOCATION e flag no userModel)
   * sem restaurar ORIGINAL em LAST_LOCATION. Usar quando a assinatura expira mas a localização
   * atual já é a mesma da teleportada (não precisa "voltar para casa").
   */
  public async clearTeleportationStateOnly(userModel: UserModel): Promise<void> {
    this.teleportLocation = null;
    await Preferences.remove({ key: 'TELEPORT_LOCATION' });
    await Preferences.remove({ key: 'ORIGINAL_LOCATION' });
    userModel.usingTeleportation = false;
    await Preferences.set({ key: 'USER_DATA', value: JSON.stringify(userModel) });
    console.log('Teleportation state cleared (subscription expired, same location)');
  }

  /** Guarda a localização original (casa) antes de ativar o teletransporte, para restaurar quando acabar. */
  public async saveOriginalLocationBeforeTeleport(): Promise<void> {
    const location = this.lastLocation || (await Preferences.get({ key: 'LAST_LOCATION' })).value;
    if (location) {
      const parsed = typeof location === 'string' ? JSON.parse(location) : location;
      if (parsed && parsed.coordinates) {
        await Preferences.set({ key: 'ORIGINAL_LOCATION', value: JSON.stringify(parsed) });
        console.log('ORIGINAL_LOCATION saved before teleport');
      }
    }
  }

  /** Retorna a localização original guardada (para exibir "casa" na modal de teletransporte). */
  public async getOriginalLocation(): Promise<{ type: string; coordinates: [number, number]; address?: string } | null> {
    const result = await Preferences.get({ key: 'ORIGINAL_LOCATION' });
    const val = result.value ? JSON.parse(result.value) : null;
    return val && val.coordinates ? val : null;
  }

  private static teleportBackupKey(userId: string): string {
    return `TELEPORT_BACKUP_${userId}`;
  }

  /** Normaliza userId a partir de userData (id, _id ou _id.$oid) para chave de backup consistente. */
  public static getUserIdFromUser(userData: any): string | null {
    if (!userData) return null;
    const raw = userData.id ?? userData._id?.$oid ?? userData._id;
    if (raw == null) return null;
    const s = String(raw).trim();
    return s.length > 0 ? s : null;
  }

  /**
   * Chamar no logout: reseta o teletransporte para o próximo usuário não herdar a localização
   * teleportada. Se userId for passado, salva a localização teleportada em backup para o mesmo
   * usuário restaurar ao logar de novo. Restaura ORIGINAL_LOCATION em LAST_LOCATION (se existir)
   * e limpa TELEPORT_LOCATION e ORIGINAL_LOCATION.
   */
  public async resetTeleportationOnLogout(userId?: string | null): Promise<void> {
    const normalizedId = userId != null ? String(userId).trim() : '';
    if (normalizedId && this.teleportLocation) {
      await Preferences.set({
        key: LocationService.teleportBackupKey(normalizedId),
        value: JSON.stringify(this.teleportLocation)
      });
      console.log('Teleportation backed up for user on logout, key: TELEPORT_BACKUP_' + normalizedId);
    }
    const original = await this.getOriginalLocation();
    if (original != null && original.coordinates) {
      this.lastLocation = original;
      await Preferences.set({ key: 'LAST_LOCATION', value: JSON.stringify(original) });
      console.log('ORIGINAL_LOCATION restored to LAST_LOCATION on logout');
    }
    this.teleportLocation = null;
    await Preferences.remove({ key: 'TELEPORT_LOCATION' });
    await Preferences.remove({ key: 'ORIGINAL_LOCATION' });
    console.log('Teleportation reset on logout');
  }

  /** Remove teletransporte e restaura a localização original em LAST_LOCATION e lastLocation. */
  public async removeTeleportationLocation(userModel: UserModel): Promise<void> {
    this.teleportLocation = null;
    await Preferences.remove({ key: 'TELEPORT_LOCATION' });
    console.log('teleportLocation removed from Preferences');

    const original = await Preferences.get({ key: 'ORIGINAL_LOCATION' });
    const originalVal = original.value ? JSON.parse(original.value) : null;
    if (originalVal && originalVal.coordinates) {
      this.lastLocation = originalVal;
      await Preferences.set({ key: 'LAST_LOCATION', value: JSON.stringify(originalVal) });
      await Preferences.remove({ key: 'ORIGINAL_LOCATION' });
      console.log('ORIGINAL_LOCATION restored to LAST_LOCATION');
    }

    userModel.usingTeleportation = false;
    await Preferences.set({ key: 'USER_DATA', value: JSON.stringify(userModel) });
    console.log('userModel saved to Preferences');
  }

  public resetLocationRefresher() {
    this.unSubscribeLocationRefresher();
    this.subscribeLocationRefresher();
  }

  public unSubscribeLocationRefresher() {
    this.locRefreshSubscription.unsubscribe();
  }

  private lastLocationSet = false;

  // async testEcho() {
  //   const echoResult = await GeolocationExtras.echo({ value: 'Hello World PLUGIN RODANDO BALA' });
  //   console.log('Echo result:', echoResult.value);
  // }
  
  
  


  public async subscribeLocationRefresher() {
    // Função para salvar a última localização padrão
    const setDefaultLocation = async () => {
      if (!this.lastLocationSet) {
        await Preferences.set({ key: 'LAST_LOCATION', value: JSON.stringify(environment.locationDefault) });
        console.log('lastLocation saved to Preferences');
        this.lastLocationSet = true;
      }
    };
  
    // Verifica o status atual das permissões
    if(!Capacitor.isNativePlatform()){
      await setDefaultLocation();
      return;
    }
    let permissions = await Geolocation.checkPermissions();

    // Solicita permissões se ambas forem "denied"
    if (permissions.coarseLocation === 'denied' && permissions.location === 'denied') {
      await setDefaultLocation();
    } else {
      // Solicita coarseLocation se não for "granted" ou "denied"
      if (permissions.coarseLocation !== 'granted' && permissions.coarseLocation !== 'denied') {
        permissions = await Geolocation.requestPermissions({ permissions: ['coarseLocation'] });
        if (permissions.coarseLocation !== 'granted') {
          await setDefaultLocation();
        }
      }
  
      // Solicita location se não for "granted" ou "denied"
      if (permissions.location !== 'granted' && permissions.location !== 'denied') {
        permissions = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (permissions.location !== 'granted') {
          await setDefaultLocation();
        }
      }
    }
  
    // Inicia a atualização da localização se uma das permissões foi concedida
    if (permissions.coarseLocation === 'granted' || permissions.location === 'granted') {
      this.unSubscribeLocationRefresher();
      this.getRead();
      this.locRefreshSubscription = interval(10000).subscribe(() => {
        this.getRead();
      });
    } else {
      this.unSubscribeLocationRefresher();
    }
  }
  

  /**
   * Returns true when the user has granted any form of location permission.
   * Used by callers (ex.: home page) para distinguir "permissão negada" de
   * "permissão concedida mas GPS ainda não respondeu" antes de mostrar toast
   * de erro. Retorna false se a checagem em si lançar (ex.: Location Services
   * desabilitado no sistema).
   */
  public async hasLocationPermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }
    try {
      const perm = await Geolocation.checkPermissions();
      return perm.location === 'granted' || perm.coarseLocation === 'granted';
    } catch {
      return false;
    }
  }

  private async getRead() {
    try {
      const gpsOptions = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 60000,
      };
      const resp = await Geolocation.getCurrentPosition(gpsOptions);
      const lat = resp.coords.latitude;
      const lon = resp.coords.longitude;
      const locationJson = { type: 'Point', coordinates: [lon, lat] };
      this.lastLocation = locationJson;
      console.log('LocationService Storing location object: ' + JSON.stringify(locationJson));
      Preferences.set({ key: 'LAST_LOCATION', value: JSON.stringify(this.lastLocation) }).then(() => {
        console.log('lastLocation saved to Preferences');
      });

      localStorage.setItem('isLocationEnabled', 'true');

      this.storeLocationTouserModel(locationJson);

    } catch (ex) {
      Preferences.set({ key: 'LAST_LOCATION', value: JSON.stringify(environment.locationDefault) }).then(() => {
        console.log('lastLocation saved to Preferences');
      });
      console.log('Error getting location: ', ex);
      localStorage.setItem('isLocationEnabled', 'false');
    }
  }

  private async storeLocationTouserModel(locationJson: any) {
    const userModelJson = await Preferences.get({ key: 'USER_DATA' });
    if (userModelJson && userModelJson.value) {
      this.userModel = JSON.parse(userModelJson.value);
      // Only overwrite location if user is NOT using teleportation
      if (!this.userModel.usingTeleportation) {
        this.userModel.location = locationJson;
        Preferences.set({ key: 'USER_DATA', value: JSON.stringify(this.userModel) }).then(() => {
          console.log('userModel saved to Preferences with new location');
        });
      } else {
        console.log('storeLocationTouserModel: User is using teleportation, preserving teleported location');
      }
    }
  }
}
